import * as pdfjsLib from 'pdfjs-dist';
import { useEffect, useRef, useState } from 'react';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

type PageElementsMap = Map<number, HTMLDivElement>;
export const PDF_SCALE = 1.25;
const MAX_DOCUMENT_CANVAS_PIXELS = 48_000_000;
export const ZOOM_RENDER_DELAY_MS = 200;

export function usePdfRenderer(blob: Blob, scale: number = PDF_SCALE) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageElements, setPageElements] = useState<PageElementsMap>(new Map());
  const [pdfDocument, setPdfDocument] =
    useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isRendering, setIsRendering] = useState(true);
  const previousScaleRef = useRef<number>(scale);

  // Separate effect for loading PDF (only when blob changes)
  useEffect(() => {
    let cancelled = false;
    let loadedPdf: pdfjsLib.PDFDocumentProxy | null = null;

    const loadPdf = async () => {
      setHasError(false);
      setIsRendering(true);
      const pdf = await loadPdfDocument(blob);
      if (cancelled) {
        await pdf.loadingTask.destroy();
        return;
      }
      loadedPdf = pdf;
      setPdfDocument(pdf);
      setTotalPages(pdf.numPages);
    };

    loadPdf().catch(() => {
      if (!cancelled) setHasError(true);
    });

    return () => {
      cancelled = true;
      loadedPdf?.loadingTask.destroy().catch(() => undefined);
    };
  }, [blob]);

  // Initial render effect (only when PDF document changes)
  useEffect(() => {
    if (!containerRef.current || !pdfDocument) return;

    let cancelled = false;

    const renderPdf = async () => {
      if (cancelled) return;

      clearContainer(containerRef.current!);

      const elements = await renderPages(
        pdfDocument,
        containerRef.current!,
        PDF_SCALE, // Always render at base scale
        () => cancelled,
        () => setIsRendering(false),
      );

      if (!cancelled) {
        setPageElements(elements);
        setIsRendering(false);
      }
    };

    renderPdf().catch(() => {
      // Renders in flight fail once the document is destroyed
      if (cancelled || pdfDocument.loadingTask.destroyed) return;
      setHasError(true);
    });

    return () => {
      cancelled = true;
    };
  }, [pdfDocument]);

  // Apply CSS scaling when scale changes: instant feedback until the pages are redrawn
  useEffect(() => {
    if (pageElements.size === 0) return;

    const container = containerRef.current?.parentElement;
    if (!container) return;

    // Save current scroll position and calculate center point
    const scrollTop = container.scrollTop;
    const scrollCenter = scrollTop + container.clientHeight / 2;

    const scaleRatio = scale / PDF_SCALE;
    const previousScaleRatio = previousScaleRef.current / PDF_SCALE;

    pageElements.forEach((pageElement) => {
      // Get the original dimensions from the canvas inside
      const canvas = pageElement.querySelector('canvas');
      if (!canvas) return;

      const { width: originalWidth, height: originalHeight } =
        getPageCanvasBaseSize(canvas);

      // Apply transform to the canvas
      canvas.style.transform = `scale(${scaleRatio})`;
      canvas.style.transformOrigin = 'top left';

      // Adjust the wrapper dimensions to match the scaled size
      pageElement.style.width = `${originalWidth * scaleRatio}px`;
      pageElement.style.height = `${originalHeight * scaleRatio}px`;
    });

    // Use requestAnimationFrame to adjust scroll after DOM updates
    requestAnimationFrame(() => {
      // Adjust scroll position proportionally to maintain visual position
      // The ratio of scale change tells us how much to adjust the scroll
      const scaleChangeRatio = scaleRatio / previousScaleRatio;
      const newScrollCenter = scrollCenter * scaleChangeRatio;
      const newScrollTop = newScrollCenter - container.clientHeight / 2;

      container.scrollTop = newScrollTop;
    });

    // Update previous scale
    previousScaleRef.current = scale;
  }, [pageElements, scale]);

  // Redraw the pages once zooming settles: the CSS scale alone stretches the bitmap and blurs it
  useEffect(() => {
    if (!pdfDocument || pageElements.size === 0) return;

    let cancelled = false;
    const timeout = setTimeout(() => {
      // A failed redraw keeps the current, stretched canvas: nothing to report
      redrawPagesForScale(
        pdfDocument,
        pageElements,
        scale,
        () => cancelled,
      ).catch(() => undefined);
    }, ZOOM_RENDER_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [pdfDocument, pageElements, scale]);

  return {
    containerRef,
    pageElements,
    pdfDocument,
    totalPages,
    hasError,
    isRendering,
  };
}

async function loadPdfDocument(blob: Blob) {
  const arrayBuffer = await blob.arrayBuffer();
  return await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
}

async function renderPages(
  pdf: pdfjsLib.PDFDocumentProxy,
  container: HTMLDivElement,
  scale: number,
  isCancelled: () => boolean,
  onPageRendered: () => void,
): Promise<PageElementsMap> {
  const elements: PageElementsMap = new Map();
  let outputScale: number | undefined;

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    // Check if cancelled before rendering each page
    if (isCancelled()) {
      break;
    }

    const page = await pdf.getPage(pageNumber);
    outputScale ??= getOutputScale(page.getViewport({ scale }), pdf.numPages);
    const pageElement = await renderSinglePage(page, scale, outputScale);

    // Check again after async operation
    if (isCancelled()) {
      break;
    }

    container.appendChild(pageElement);
    elements.set(pageNumber, pageElement);
    onPageRendered();
  }

  return elements;
}

/**
 * Replaces each page canvas with one drawn at the zoomed resolution. The layout keeps using the
 * base size and the CSS transform, so only the bitmap density changes.
 */
async function redrawPagesForScale(
  pdf: pdfjsLib.PDFDocumentProxy,
  pageElements: PageElementsMap,
  scale: number,
  isCancelled: () => boolean,
): Promise<void> {
  const zoomedPixelRatio = (window.devicePixelRatio || 1) * (scale / PDF_SCALE);
  let outputScale: number | undefined;

  for (const [pageNumber, pageElement] of pageElements) {
    if (isCancelled()) return;

    const currentCanvas = pageElement.querySelector('canvas');
    if (!currentCanvas) continue;

    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: PDF_SCALE });
    outputScale ??= getOutputScale(viewport, pdf.numPages, zoomedPixelRatio);
    if (currentCanvas.width === Math.floor(viewport.width * outputScale)) {
      continue;
    }

    const canvas = await renderPageCanvas(page, viewport, outputScale);
    if (isCancelled()) return;

    canvas.style.transform = currentCanvas.style.transform;
    canvas.style.transformOrigin = currentCanvas.style.transformOrigin;
    currentCanvas.replaceWith(canvas);
  }
}

/**
 * Canvas pixels per CSS pixel: the device pixel ratio for sharp pages, lowered (never below 1)
 * when the whole document would exceed the canvas memory budget.
 */
export function getOutputScale(
  pageSize: { width: number; height: number },
  pageCount: number,
  devicePixelRatio: number = window.devicePixelRatio || 1,
): number {
  const documentPixels = pageSize.width * pageSize.height * pageCount;
  const budgetScale = Math.sqrt(MAX_DOCUMENT_CANVAS_PIXELS / documentPixels);
  return Math.max(1, Math.min(devicePixelRatio, budgetScale));
}

export async function renderSinglePage(
  page: pdfjsLib.PDFPageProxy,
  scale: number,
  outputScale: number,
): Promise<HTMLDivElement> {
  const viewport = page.getViewport({ scale });

  const wrapper = createPageWrapper(viewport);
  const canvas = await renderPageCanvas(page, viewport, outputScale);

  wrapper.appendChild(canvas);
  return wrapper;
}

async function renderPageCanvas(
  page: pdfjsLib.PDFPageProxy,
  viewport: pdfjsLib.PageViewport,
  outputScale: number,
): Promise<HTMLCanvasElement> {
  const canvas = createPageCanvas(viewport, outputScale);

  const context = canvas.getContext('2d')!;
  await page.render({
    canvasContext: context,
    viewport,
    canvas,
    transform:
      outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
  }).promise;

  return canvas;
}

export function getPageCanvasBaseSize(canvas: HTMLCanvasElement): {
  width: number;
  height: number;
} {
  return {
    width: Number(canvas.dataset.baseWidth ?? canvas.width),
    height: Number(canvas.dataset.baseHeight ?? canvas.height),
  };
}

function createPageWrapper(viewport: pdfjsLib.PageViewport): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'relative mb-4';
  wrapper.style.width = `${viewport.width}px`;
  wrapper.style.height = `${viewport.height}px`;
  return wrapper;
}

function createPageCanvas(
  viewport: pdfjsLib.PageViewport,
  outputScale: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width * outputScale);
  canvas.height = Math.floor(viewport.height * outputScale);
  canvas.style.width = `${viewport.width}px`;
  canvas.style.height = `${viewport.height}px`;
  canvas.dataset.baseWidth = String(viewport.width);
  canvas.dataset.baseHeight = String(viewport.height);
  canvas.className = 'shadow-lg';
  return canvas;
}

function clearContainer(container: HTMLDivElement): void {
  container.innerHTML = '';
}
